import { updateStoreApi } from '@apis/seller-center/profile-settings';
import { Button, Group, Input, Modal, Toast } from '@common-web/index';
import { strings } from '@constants/strings';
import { updateStoreInfo } from '@reducers/seller-center/userinfo-slice';
import { isCP } from '@utils/app-utils';
import { getErrorString } from '@utils/utils';
import { Alert, Flex, Space, Typography } from 'antd';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';
import tenantConfig from '../../../data/customizer/tenant-config';

const { Text, Paragraph } = Typography;

const initialValues = (taxInfo) => ({
  ntn: taxInfo?.tax_number || '',
  strn: taxInfo?.strn || '',
});

const validationSchema = () =>
  yup.object({
    ntn: yup.string().required(strings.field_not_valid_msg).min(7, 'Minimum 7 characters are required'),
    strn: yup.string().required(strings.field_not_valid_msg).min(7, 'Minimum 7 characters are required'),
  });

const TaxVerificationModal = () => {
  const authorized = useSelector((state) => state?.userData?.authorized);
  const { id, tax_number, strn, govt_registration_status, owner } = useSelector(
    (state) => state?.userData?.userInfo?.userProfile?.store || {}
  );

  const location = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [ntnVerificationModal, setNtnVerificationModal] = useState(false);

  useEffect(() => {
    if (!tax_number || !strn || govt_registration_status === 'rejected') {
      setNtnVerificationModal(true);
    }
  }, [location?.pathname, tax_number, strn]);

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const body = {
        store: {
          tax_number: values?.ntn,
          strn: values?.strn,
          owner_attributes: {
            bank_id: owner?.bank?.id,
            account_name: owner?.account_name,
          },
        },
      };
      const res = await updateStoreApi(id, body);
      if (res.success) {
        closeModal();
        dispatch(
          updateStoreInfo({
            tax_number: values?.ntn,
            strn: values?.strn,
            govt_registration_status: 'pending',
          })
        );
      }
    } catch (e) {
      Toast.error(getErrorString(e));
    }
    setLoading(false);
  };

  const closeModal = () => {
    setNtnVerificationModal(false);
  };

  if (isCP() || !authorized || !tenantConfig?.tax_verification_enabled) {
    return;
  }

  return (
    <Modal
      open={ntnVerificationModal}
      title={strings.ntn_strn_required}
      okText={strings.confirm}
      width={'672px'}
      zIndex={9999}
      handleCancel={closeModal}
      maskClosable={false}
      closeByKeyboard={false}
      closable={true}
      showActions={false}
      ModalBody={
        <TaxVerificationModalBody
          closeModal={closeModal}
          onSubmit={onSubmit}
          loading={loading}
          taxInfo={{ tax_number, strn, name: owner?.name }}
        />
      }
    />
  );
};

const TaxVerificationModalBody = ({ onSubmit, loading, taxInfo }) => {
  return (
    <div>
      <Paragraph className={'fs-14'} style={{ color: '#406367', marginBottom: 14 }}>
        As per new government regulations, you must provide your{' '}
        <Text style={{ color: 'var(--ant-color-primary)' }} strong>
          NTN (Income Tax Registration Number)
        </Text>{' '}
        &{' '}
        <Text style={{ color: 'var(--ant-color-primary)' }} strong>
          STRN (Sales Tax Registration Number)
        </Text>{' '}
        to continue using the Seller Center. Without these, you won't be able to take any actions on your account.
      </Paragraph>

      <Alert
        message={
          <Space className={'lineHeight1'}>
            <Text className={'fs-14'} style={{ color: '#002F34', lineHeight: 1 }}>
              Please note that if this requirement is not met, there may be an initial fine of{' '}
              <Text strong>Rs. 500,000</Text>, with the potential for additional fines up to{' '}
              <Text strong>Rs. 1,000,000</Text>, as per government.
            </Text>
          </Space>
        }
        type="error"
        showIcon
        icon={<AiOutlineExclamationCircle size={20} />}
        style={{
          backgroundColor: '#FFEEEA',
          border: '1px solid #FFEBEE',
          marginBottom: 16,
        }}
      />

      <Formik
        enableReinitialize
        initialValues={initialValues(taxInfo)}
        onSubmit={onSubmit}
        validationSchema={validationSchema()}
      >
        {(formikProps) => {
          const { values, setFieldValue, dirty, errors, touched, handleSubmit } = formikProps;

          return (
            <form>
              <Group template={'repeat(2, 1fr)'} gap={'24px 48px'} className={'mbe-16'}>
                <Input
                  label={strings.ntn}
                  placeholder={strings.enter_ntn}
                  value={values?.ntn}
                  onChange={(e) => {
                    setFieldValue('ntn', e.target.value?.trim());
                  }}
                  errorMsg={touched?.ntn && errors?.ntn && errors?.ntn}
                  required={true}
                />
                <Input
                  label={strings.strn}
                  placeholder={strings.enter_strn}
                  value={values?.strn}
                  onChange={(e) => {
                    setFieldValue('strn', e.target.value?.trim());
                  }}
                  errorMsg={touched?.strn && errors?.strn && errors?.strn}
                  required={true}
                />
              </Group>
              <Paragraph className={'fs-14'} style={{ color: '#406367' }}>
                By proceeding, I <Text strong>{taxInfo?.name}</Text> confirm that the NTN & STRN I’ve provided are
                correct and that I am registered with the Federal Board of Revenue (FBR). I understand that providing
                incorrect information may lead to legal penalties.
              </Paragraph>
              <Flex justify="end" gap={8} className={'mt-16'}>
                <Button
                  text={strings.update}
                  type="primary"
                  loading={loading}
                  onClick={() => {
                    handleSubmit();
                  }}
                  disabled={!dirty || loading}
                />
              </Flex>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default TaxVerificationModal;
